import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useState } from "react";
import { License, LicenseTypeEnum } from "../types/license";
import { ProteinRow } from "../types/protein-row";
import { ProteinResponse } from "../../../api/openapi-generator";
import { useProteinRows } from "./use-protein-rows";
import { IMutation, MutationLevelEnum } from "../types/mutation";
import { useCodonMutations } from "./use-codon-mutations";

type InputContextType = ReturnType<typeof useProviderValue>;

const InputContext = createContext<InputContextType | undefined>(undefined);

export const useInputContext = () => {
  const context = useContext(InputContext);
  if (context === undefined) {
    throw new Error("useInputContext must be used within an InputContextProvider");
  }
  return context;
};

export function InputContextProvider({
  children,
}: {
  children: ReactNode,
}) {
  const value = useProviderValue();
  return (
    <InputContext.Provider value={value}>
      {children}
    </InputContext.Provider>
  );
}

function useProviderValue() {

  // Constants:

  // mutations on multiple positions, we'll change this to true later
  const multiplePos: boolean = false;
  // multiple mutations for one position, this would lead to multiple mutants
  const multipleUnits: boolean = false;
  // we support only the protein-level mutations
  const level = MutationLevelEnum.Protein;

  // State:

  const [emailField, setEmailField] = useState("");
  const [vinaJobDesiredRun, setVinaJobDesiredRun] = useState(false);
  const [licenseForm, setLicenseForm] = useState<License>({
    license: LicenseTypeEnum.ACADEMIC,
    commercial: {
      rosetta: false,
      foldx: false,
    }
  });

  // Custom hooks:

  const {targets, failed, rows} = useProteinRows();

  const {
    rowSelected,
    targetSelected,
    mutations,
    setRowSelected,
    setTargetSelected,
    setMutations,
  } = useSelection();

  const sequence = targetSelected?.sequence ?? '';

  const codonMutations = useCodonMutations(level, sequence, mutations);

  return {
    multiplePos, multipleUnits, level,

    emailField, setEmailField,
    licenseForm, setLicenseForm,

    targets, failed,
    rows,

    sequence,

    rowSelected,
    targetSelected,
    mutations,
    setRowSelected,
    setTargetSelected,
    setMutations,

    codonMutations,

    vinaJobDesiredRun,
    setVinaJobDesiredRun,
  }
}

function useSelection() {

  const [selection, _setSelection] = useState<{
    row: ProteinRow | null,
    target: ProteinResponse | null,
    mutations: IMutation[],
  }>({
    row: null,
    target: null,
    mutations: [],
  });

  const setSelection: Dispatch<SetStateAction<typeof selection>> = useCallback(updater => {
    _setSelection(prevSelection => {
      let newSelection = typeof updater === 'function' ? updater(prevSelection) : updater;
      // if row is unselected, target should be deselected
      if (!newSelection.row && newSelection.target) {
        newSelection = {...newSelection, target: null};
      }
      // if target is unselected, mutations should be deselected
      if (!newSelection.target && newSelection.mutations.length > 0) {
        newSelection = {...newSelection, mutations: []};
      }
      return newSelection;
    });
  }, []);

  const setRowSelected = useCallback((row: ProteinRow | null) => {
    setSelection({
      row: row ?? null,
      target: row && row.proteins.length === 1 ? row.proteins[0] : null,
      mutations: [],
    });
  }, [setSelection]);

  const setTargetSelected = useCallback((target: ProteinResponse | null) => {
    setSelection(selection => ({
      row: selection.row,
      target: target ?? null,
      mutations: [],
    }))
  }, [setSelection]);

  const setMutations: Dispatch<SetStateAction<IMutation[]>> = useCallback(updater => {
    setSelection(selection => {
      const mutations = typeof updater === 'function' ? updater(selection.mutations) : updater;
      return {
        ...selection,
        mutations: mutations,
      }
    })
  }, [setSelection]);

  return {
    rowSelected: selection.row,
    targetSelected: selection.target,
    mutations: selection.mutations,
    setRowSelected,
    setTargetSelected,
    setMutations,
  }
}
